<template>
  <div>
    <b-card v-if="stripData">
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h3>{{ stripData.name }}</h3>
        <div>
          <b-form-rating
            :value="stripData.difficulty"
            no-border
            :stars="5"
            variant="warning"
            @change="onDifficultyChange"
          />
        </div>
      </div>
      <p class="text-muted mb-1">
        Автор: {{ stripData.author_name || '—' }}, шкала:
        <b-badge>{{ stripData.scale_type === 'flat' ? `плоская (${stripData.base_price})` : 'возрастающая' }}</b-badge>
        <b-badge variant="info" class="ml-1">{{ filledCount }}/5</b-badge>
      </p>

      <draggable
        v-model="slots"
        handle=".drag-handle"
        :options="{ animation: 200 }"
        @end="onReorder"
      >
        <b-card
          v-for="(slot, index) in slots"
          :key="`slot-${index}`"
          class="mb-1 strip-slot-row"
          no-body
        >
          <div class="d-flex align-items-center p-1">
            <feather-icon
              v-if="slot && slot.question"
              icon="GridIcon"
              class="drag-handle cursor-move mr-1 text-muted"
            />
            <div class="flex-grow-1">
              <div v-if="slot && slot.question">
                <b-badge variant="primary" class="mr-1">{{ slot.price }}</b-badge>
                <strong>{{ slot.question.answer || '—' }}</strong>
                <small class="text-muted ml-1">
                  ({{ formatLabel(slot.question.format) }}, частей: {{ slot.question.parts ? slot.question.parts.length : 0 }})
                </small>
                <div v-if="slot.question.artists && slot.question.artists.length" class="mt-25">
                  <b-badge v-for="a in slot.question.artists" :key="a.id" variant="light-info" class="mr-25">
                    {{ a.title }}
                  </b-badge>
                </div>
              </div>
              <div v-else class="text-muted">
                <em>Свободно (слот #{{ index + 1 }})</em>
              </div>
            </div>
            <div class="d-flex" style="gap: 4px">
              <b-button
                v-if="slot && slot.question"
                size="sm"
                variant="outline-primary"
                @click="toggleEdit(slot.question.id)"
              >
                <feather-icon :icon="isOpen(slot.question.id) ? 'ChevronUpIcon' : 'EditIcon'" />
                {{ isOpen(slot.question.id) ? 'Свернуть' : 'Редактировать' }}
              </b-button>
              <b-button
                v-if="slot && slot.question"
                size="sm"
                variant="outline-danger"
                @click="onRemove(slot.question.id)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
              <b-button
                v-if="!(slot && slot.question)"
                size="sm"
                variant="outline-primary"
                @click="toggleFill(index)"
              >
                <feather-icon :icon="openSlotIndex === index ? 'ChevronUpIcon' : 'PlusIcon'" />
                {{ openSlotIndex === index ? 'Свернуть' : 'Заполнить' }}
              </b-button>
            </div>
          </div>
          <b-collapse v-if="slot && slot.question" :visible="isOpen(slot.question.id)">
            <strip-question-editor
              :strip-id="stripId"
              :question="slot.question"
              :lookups="lookups"
              @updated="loadStrip"
            />
          </b-collapse>
          <b-collapse v-else :visible="openSlotIndex === index">
            <div class="p-1">
              <strip-builder-question-step
                :strip-id="stripId"
                :question-index="index + 1"
                :strip-format="stripData.format"
                :scale-type="stripData.scale_type"
                :base-price="stripData.base_price"
                :is-saving="isSaving"
                @accepted="p => onQuestionAccepted(p, index)"
                @accepted-manual="p => onManualQuestionAccepted(p, index)"
              />
            </div>
          </b-collapse>
        </b-card>
      </draggable>
    </b-card>

    <div v-else class="text-center py-3">
      <b-spinner variant="primary" />
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BBadge, BSpinner, BFormRating, BCollapse,
} from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'
import store from '@/store'
import stripBuilderStoreModule from './builder/stripBuilderStoreModule'
import StripQuestionEditor from './StripQuestionEditor.vue'
import StripBuilderQuestionStep from './builder/StripBuilderQuestionStep.vue'

const FORMAT_LABELS = {
  guess_melody: 'Угадай мелодию',
  cover_art: 'Обложка',
  cover_song: 'Кавер',
}

export default {
  components: {
    BCard, BButton, BBadge, BSpinner, BFormRating, BCollapse, draggable,
    StripQuestionEditor, StripBuilderQuestionStep,
  },
  setup() {
    const STORE_MODULE_NAME = 'strip-builder'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, stripBuilderStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const stripId = ref(router.currentRoute.params.id)
    const stripData = ref(null)
    const slots = ref(new Array(5).fill(null))
    const openEditors = ref([])
    const openSlotIndex = ref(null) // индекс пустого слота, для которого открыт inline-builder
    const lookups = ref({ genres: [], decades: [], origins: [] })
    const isSaving = ref(false)

    const isOpen = id => openEditors.value.includes(id)
    const toggleEdit = id => {
      const idx = openEditors.value.indexOf(id)
      if (idx >= 0) openEditors.value.splice(idx, 1)
      else openEditors.value.push(id)
    }
    const toggleFill = index => {
      openSlotIndex.value = openSlotIndex.value === index ? null : index
    }

    const filledCount = computed(() => slots.value.filter(s => s && s.question).length)

    const rebuildSlots = () => {
      const newSlots = new Array(5).fill(null)
      ;(stripData.value.strip_questions || []).forEach(sq => {
        const idx = sq.order - 1
        if (idx >= 0 && idx < 5) {
          newSlots[idx] = {
            id: sq.id,
            order: sq.order,
            price: sq.price,
            question: sq.question,
          }
        }
      })
      slots.value = newSlots
    }

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    }

    const loadStrip = () => {
      store.dispatch('strip-builder/loadStrip', { id: stripId.value })
        .then(response => {
          stripData.value = response.data
          rebuildSlots()
        })
        .catch(() => showToast('Ошибка загрузки', 'danger'))
    }

    const loadLookups = () => {
      store.dispatch('strip-builder/fetchLookups')
        .then(response => { lookups.value = response.data || {} })
        .catch(() => {})
    }

    loadStrip()
    loadLookups()

    const onQuestionAccepted = (payload, slotIndex) => {
      isSaving.value = true
      const order = slotIndex + 1
      store.dispatch('strip-builder/acceptQuestion', {
        stripId: stripId.value,
        payload: { ...payload, order },
      })
        .then(() => {
          showToast('Вопрос сохранён', 'success')
          openSlotIndex.value = null
          loadStrip()
        })
        .catch(error => {
          const msg = error.response?.data?.message || 'Ошибка сохранения'
          showToast(msg, 'danger')
        })
        .finally(() => { isSaving.value = false })
    }

    const onManualQuestionAccepted = ({ formData }, slotIndex) => {
      isSaving.value = true
      formData.append('order', slotIndex + 1)
      store.dispatch('strip-builder/acceptManualQuestion', {
        stripId: stripId.value,
        formData,
      })
        .then(() => {
          showToast('Вопрос сохранён', 'success')
          openSlotIndex.value = null
          loadStrip()
        })
        .catch(error => {
          const msg = error.response?.data?.message || 'Ошибка сохранения'
          showToast(msg, 'danger')
        })
        .finally(() => { isSaving.value = false })
    }

    const formatLabel = format => FORMAT_LABELS[format] || '—'

    const onRemove = questionId => {
      if (!window.confirm('Удалить вопрос?')) return
      store.dispatch('strip-builder/deleteQuestion', {
        stripId: stripId.value,
        questionId,
      })
        .then(() => {
          showToast('Вопрос удалён', 'success')
          loadStrip()
        })
        .catch(() => showToast('Ошибка удаления', 'danger'))
    }

    const onReorder = () => {
      const order = {}
      slots.value.forEach((slot, idx) => {
        if (slot && slot.question) {
          order[slot.question.id] = idx + 1
        }
      })
      if (Object.keys(order).length === 0) return
      store.dispatch('strip-builder/reorderQuestions', {
        stripId: stripId.value,
        order,
      })
        .then(() => {
          showToast('Порядок обновлён', 'success')
          loadStrip()
        })
        .catch(() => {
          showToast('Ошибка сохранения порядка', 'danger')
          loadStrip()
        })
    }

    const onDifficultyChange = value => {
      store.dispatch('strip-builder/updateStrip', {
        id: stripId.value,
        payload: { difficulty: value },
      })
        .then(() => showToast('Сложность обновлена', 'success'))
        .catch(() => showToast('Ошибка обновления', 'danger'))
    }

    return {
      stripId, stripData, slots, filledCount, lookups, isSaving,
      formatLabel, onRemove, onReorder, onDifficultyChange,
      isOpen, toggleEdit, loadStrip,
      openSlotIndex, toggleFill, onQuestionAccepted, onManualQuestionAccepted,
    }
  },
}
</script>

<style scoped>
.strip-slot-row {
  border: 1px solid #ebe9f1;
}
.drag-handle {
  cursor: move;
}
</style>
